import {
    Component,
    EventEmitter,
    forwardRef,
    Injector,
    Input,
    OnDestroy,
    Output,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
} from '@angular/forms';
import { CmpsDialog, CmpsDialogRef } from '@cmps/ng-uikit';
import { filter, tap } from 'rxjs/operators';

import { FocusDto } from '../../coaching-base/general';
import { GoalsCollection } from '../../coaching-commitment/goals-collection.model';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';

import { FocusSelectorComponent } from './focus-selector.component';
import { FocusSelectorDialogData } from './model/focus-selector-dialog-data.model';
import { FocusSelectorDialogResult } from './model/focus-selector-dialog-result.model';
import { FocusSelectorFocusForPerformer } from './model/focus-selector-focus-for-performer.model';
import { FocusSelectorTriggerPayload } from './model/focus-selector-trigger-payload.model';
import { FocusSelectorTriggerSource } from './model/focus-selector-trigger-source.enum';

@Component({
    selector: 'cmps-focus-selector-trigger',
    template: '',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FocusSelectorSetTriggerComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => FocusSelectorSetTriggerComponent),
            multi: true,
        },
    ],
})
@TakeUntilDestroy()
export class FocusSelectorSetTriggerComponent
    implements OnDestroy, ControlValueAccessor, Validator, TakeUntilDestroy
{
    @Input() storePathPrefix: string;
    @Input() triggerSource: FocusSelectorTriggerSource;
    @Input() commitmentId: number;
    @Input() performerId: number;
    @Input() selected: FocusDto;
    @Input() pinnedMetrics: number[];
    @Input() activeMetricId: number;
    @Input() goalsCollection: AbstractControl;
    @Output() save: EventEmitter<FocusDto>;
    private _onChange: (_: FocusDto) => void;
    private _onTouched: () => void;
    private _onValidatorChange: () => void;
    private _dialogRef: CmpsDialogRef<
        FocusSelectorComponent,
        FocusSelectorDialogResult
    >;

    private _lastDialogActiveCommitmentFocuses: FocusSelectorFocusForPerformer[];
    private _validationErrors: ValidationErrors;

    constructor(
        private readonly _injector: Injector,
        private readonly _dialog: CmpsDialog,
    ) {
        this.save = new EventEmitter();
    }

    ngOnInit(): void {
        //
    }

    ngOnDestroy(): void {
        this.save.complete();
        this._dialogRef?.close();
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    writeValue(obj: FocusDto): void {
        this.selected = obj;
    }

    registerOnValidatorChange(fn: () => void): void {
        this._onValidatorChange = fn;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        this._validate(
            this.commitmentId,
            this.selected,
            this._lastDialogActiveCommitmentFocuses,
        );
        return this._validationErrors;
    }

    open(payload?: FocusSelectorTriggerPayload): void {
        const data: FocusSelectorDialogData = {
            storePathPrefix: this.storePathPrefix,
            mode: null,
            focusSelectionParams: null,
            commitmentId: payload?.commitmentId ?? this.commitmentId,
            performerId: this.performerId,
            selected: this.selected,
            pinnedMetrics: this.pinnedMetrics,
            isGeneric: this.activeMetricId == null,
            activeMetricId: this.activeMetricId,
            goalsCollection: this.goalsCollection.value,
            triggerSource: payload?.triggerSource ?? this.triggerSource,
        };

        this._dialogRef = this._dialog.open(FocusSelectorComponent, {
            injector: this._injector,
            resizable: true,
            movable: true,
            disableClose: true,
            data,
        });

        this._dialogRef.result
            .pipe(
                filter((ret) => ret != null),
                tap((ret) => {
                    this.selected = ret.selected;
                    this.commitmentId = ret.commitmentId;
                    this._lastDialogActiveCommitmentFocuses =
                        ret.activeFocusesForPerformer;
                    this._validate(
                        ret.commitmentId,
                        ret.selected,
                        ret.activeFocusesForPerformer,
                    );

                    const goalsCollection: GoalsCollection = {
                        ...(this.goalsCollection?.value ?? {}),
                        commitmentKpis: ret.commitmentKpis,
                    };
                    this.goalsCollection?.setValue(goalsCollection);

                    this?._onChange(ret.selected);
                    this.save?.emit(ret.selected);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _validate(
        commitmentId: number,
        selected: FocusDto | null,
        activeFocusesForPerformer: FocusSelectorFocusForPerformer[],
    ): void {
        if (commitmentId != null) {
            return;
        }

        const obj: ValidationErrors = {};
        if (selected?.behaviourId == null) {
            obj.required = null;
        }

        if (
            activeFocusesForPerformer?.find(
                (focus) =>
                    focus.behaviourId === selected.behaviourId &&
                    focus.tacticId === selected.subBehaviourId,
            ) != null
        ) {
            obj.activeFocus = true;
        }

        this._validationErrors = Object.keys(obj).length !== 0 ? obj : null;
    }
}
